import { useEffect, useRef } from 'react'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { ErrorPage } from '@/components/shared/error-page'
import { useAuthStore } from '@/store/auth'
import {
  isChunkImportError,
  isMissingResourceError,
  isNetworkTransportError,
  isSilentQueryCancellation,
} from '@/shared/utils/network-error-utils'

export function ErrorFallback({ error }: ErrorComponentProps) {
  const router = useRouter()
  const organizationId = useAuthStore((s) => s.organizationId)

  // A query cancelled on purpose is not a failure: `activateOrg` wipes the
  // cache on every org switch, and a match suspended on one of the queries
  // that wipe destroys rethrows the cancellation here (Sentry
  // HARMONY-WEB-2Y). Re-running the match — the very thing "Réessayer" does —
  // is the whole recovery, so do it without an error page and without paging
  // anyone.
  const isCancellation = isSilentQueryCancellation(error)

  // A lazy-route chunk whose hash a Cloudflare Pages rollover retired is not an
  // incident either (Sentry HARMONY-WEB-6): `main.tsx`'s `vite:preloadError`
  // listener already showed the "nouvelle version disponible / Recharger"
  // prompt, and that reload is the only thing that can fix it. Same policy as
  // an expected HTTP status in `shouldReportQueryError` — the user has the
  // recovery, so Sentry gets nothing. The error page still renders: the import
  // did fail and the route has nothing to show.
  const isStaleChunk = isChunkImportError(error)

  // Route-loader throws land here (below <Sentry.ErrorBoundary>) and would
  // otherwise never reach Sentry. Guard against double-report: React may
  // re-render this component (e.g. StrictMode, router.invalidate retry)
  // without a new `error` reference.
  const reportedError = useRef<unknown>(null)
  useEffect(() => {
    if (reportedError.current === error) return
    reportedError.current = error
    if (isCancellation) {
      router.invalidate()
      return
    }
    if (isStaleChunk) return
    // A 404 from a loader is the id in the URL, not a bug: a stale deep link
    // or a deleted row (Sentry HARMONY-WEB-30). The user still gets the error
    // page; nobody gets paged. `shouldReportQueryError` makes the same call
    // for the query-cache bridge.
    if (isMissingResourceError(error)) return
    Sentry.captureException(error)
  }, [error, isCancellation, isStaleChunk, router])

  if (isCancellation) return null

  const isNetworkError = isNetworkTransportError(error)

  return (
    <ErrorPage
      title={
        isNetworkError ? 'Connexion interrompue' : 'Une erreur est survenue'
      }
      description={
        isNetworkError
          ? 'La connexion au service a été coupée. Veuillez réessayer.'
          : "Quelque chose s'est mal passé. Veuillez réessayer."
      }
      actions={[
        {
          label: 'Réessayer',
          onClick: () => {
            router.invalidate()
          },
        },
        {
          label: 'Retour au tableau de bord',
          variant: 'outline',
          onClick: () =>
            router.navigate(
              organizationId
                ? {
                    to: '/org/$orgId/dashboard',
                    params: { orgId: organizationId },
                  }
                : { to: '/login' }
            ),
        },
      ]}
    />
  )
}
